// Procedural sprites: houses, gum trees, a cockatoo, the bell, the bauble, the
// star, the ten decorations and the moment icons. Cheerful flat vector with
// soft glows, drawn in world units on whatever transform the caller has set.
//
// Every colour comes from the palette object (`P`), which is read from CSS
// custom properties — see lib/palette.js. No literal colours here.

import { withAlpha, mix } from './palette.js';

const TAU = Math.PI * 2;

/** Cheap deterministic hash → 0..1, for per-instance variation. */
export function jitter(n, salt = 0) {
  const x = Math.sin(n * 12.9898 + salt * 78.233) * 43758.5453;
  return x - Math.floor(x);
}

function roundRect(ctx, x, y, w, h, r) {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.lineTo(x + w - rr, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + rr);
  ctx.lineTo(x + w, y + h - rr);
  ctx.quadraticCurveTo(x + w, y + h, x + w - rr, y + h);
  ctx.lineTo(x + rr, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - rr);
  ctx.lineTo(x, y + rr);
  ctx.quadraticCurveTo(x, y, x + rr, y);
  ctx.closePath();
}

export function glow(ctx, x, y, r, color, alpha = 0.5) {
  const g = ctx.createRadialGradient(x, y, 0, x, y, r);
  g.addColorStop(0, withAlpha(color, alpha));
  g.addColorStop(1, withAlpha(color, 0));
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.arc(x, y, r, 0, TAU);
  ctx.fill();
}

function starPath(ctx, x, y, r, points = 5, inner = 0.45) {
  ctx.beginPath();
  for (let i = 0; i < points * 2; i++) {
    const rad = i % 2 === 0 ? r : r * inner;
    const a = -Math.PI / 2 + (i * Math.PI) / points;
    const px = x + Math.cos(a) * rad;
    const py = y + Math.sin(a) * rad;
    if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
  }
  ctx.closePath();
}

// ---------------------------------------------------------------------------
// Houses. `lit` is 0..1 — the window warmth and wall tint follow it.
// Variants: 0 weatherboard with verandah · 1 brick gable · 2 Queenslander on
// stilts with a wide verandah · 3 flat-roofed modern with solar panels.
// `community` draws the larger hall with a little bell tower.

export function drawHouse(ctx, P, { x, y, w = 64, h = 44, variant = 0, lit = 0, community = false, cockatoo = false, seed = 0 }) {
  const wall = mix(P.houseDark, P.houseLit, lit);
  const roof = mix(P.roofDark, P.roofLit, lit);
  const win = mix(P.windowDark, P.windowGlow, lit);
  const left = x - w / 2;
  const top = y - h;

  if (community) {
    const cw = w * 1.35;
    const ch = h * 1.15;
    const cl = x - cw / 2;
    const ct = y - ch;
    // hall
    ctx.fillStyle = wall;
    roundRect(ctx, cl, ct, cw, ch, 3);
    ctx.fill();
    // roof
    ctx.fillStyle = roof;
    ctx.beginPath();
    ctx.moveTo(cl - 6, ct + 2);
    ctx.lineTo(x, ct - ch * 0.42);
    ctx.lineTo(cl + cw + 6, ct + 2);
    ctx.closePath();
    ctx.fill();
    // bell tower
    const tw = cw * 0.22;
    ctx.fillStyle = wall;
    ctx.fillRect(x - tw / 2, ct - ch * 0.62, tw, ch * 0.3);
    ctx.fillStyle = roof;
    ctx.beginPath();
    ctx.moveTo(x - tw / 2 - 3, ct - ch * 0.62);
    ctx.lineTo(x, ct - ch * 0.85);
    ctx.lineTo(x + tw / 2 + 3, ct - ch * 0.62);
    ctx.closePath();
    ctx.fill();
    drawBell(ctx, P, x, ct - ch * 0.48, tw * 0.28, lit);
    // tall windows
    if (lit > 0.05) glow(ctx, x, ct + ch * 0.55, cw * 0.7, P.windowGlow, 0.25 * lit);
    ctx.fillStyle = win;
    for (let i = 0; i < 4; i++) {
      const wx = cl + cw * (0.14 + i * 0.24);
      roundRect(ctx, wx, ct + ch * 0.3, cw * 0.12, ch * 0.42, 3);
      ctx.fill();
    }
    // door
    ctx.fillStyle = P.door;
    roundRect(ctx, x - 6, y - 16, 12, 16, 2);
    ctx.fill();
    if (cockatoo) drawCockatoo(ctx, P, cl + cw + 2, ct + 2, 0.9);
    return;
  }

  if (variant === 2) {
    // Queenslander: raised on stilts.
    const raise = 10;
    ctx.strokeStyle = P.verandah;
    ctx.lineWidth = 2.5;
    for (let i = 0; i < 4; i++) {
      const sx = left + 6 + (i * (w - 12)) / 3;
      ctx.beginPath(); ctx.moveTo(sx, y); ctx.lineTo(sx, y - raise); ctx.stroke();
    }
    ctx.fillStyle = wall;
    roundRect(ctx, left, top - raise, w, h - 2, 3);
    ctx.fill();
    // wide verandah roof
    ctx.fillStyle = roof;
    ctx.beginPath();
    ctx.moveTo(left - 10, top - raise + 8);
    ctx.lineTo(left + w * 0.5, top - raise - h * 0.55);
    ctx.lineTo(left + w + 10, top - raise + 8);
    ctx.lineTo(left + w + 10, top - raise + 12);
    ctx.lineTo(left - 10, top - raise + 12);
    ctx.closePath();
    ctx.fill();
    // verandah posts + rail
    ctx.strokeStyle = P.verandah;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(left - 8, top - raise + 12); ctx.lineTo(left - 8, y - raise + 2);
    ctx.moveTo(left + w + 8, top - raise + 12); ctx.lineTo(left + w + 8, y - raise + 2);
    ctx.moveTo(left - 8, y - raise - 10); ctx.lineTo(left + w + 8, y - raise - 10);
    ctx.stroke();
    if (lit > 0.05) glow(ctx, x, top - raise + h * 0.55, w * 0.7, P.windowGlow, 0.28 * lit);
    ctx.fillStyle = win;
    roundRect(ctx, left + w * 0.14, top - raise + h * 0.32, w * 0.22, h * 0.32, 2); ctx.fill();
    roundRect(ctx, left + w * 0.64, top - raise + h * 0.32, w * 0.22, h * 0.32, 2); ctx.fill();
    ctx.fillStyle = P.door;
    roundRect(ctx, x - 6, top - raise + h * 0.42, 12, h * 0.56, 2); ctx.fill();
    if (cockatoo) drawCockatoo(ctx, P, left + w + 10, top - raise + 8, 0.9);
    return;
  }

  if (variant === 3) {
    // Modern flat roof with solar panels.
    ctx.fillStyle = wall;
    roundRect(ctx, left, top, w, h, 3);
    ctx.fill();
    ctx.fillStyle = roof;
    roundRect(ctx, left - 4, top - 5, w + 8, 7, 2);
    ctx.fill();
    // panels
    ctx.fillStyle = P.solar;
    const pw = (w - 16) / 3;
    for (let i = 0; i < 3; i++) {
      roundRect(ctx, left + 8 + i * pw + 1, top - 12, pw - 2, 6, 1);
      ctx.fill();
    }
    ctx.strokeStyle = withAlpha(P.white, 0.25);
    ctx.lineWidth = 0.8;
    ctx.beginPath();
    ctx.moveTo(left + 8, top - 9); ctx.lineTo(left + w - 8, top - 9);
    ctx.stroke();
    if (lit > 0.05) glow(ctx, x, top + h * 0.5, w * 0.7, P.windowGlow, 0.28 * lit);
    ctx.fillStyle = win;
    roundRect(ctx, left + w * 0.1, top + h * 0.22, w * 0.5, h * 0.4, 2); ctx.fill();
    ctx.fillStyle = P.door;
    roundRect(ctx, left + w * 0.7, top + h * 0.38, w * 0.18, h * 0.62, 2); ctx.fill();
    if (cockatoo) drawCockatoo(ctx, P, left + w + 2, top - 5, 0.9);
    return;
  }

  // 0 · weatherboard with verandah, 1 · brick gable.
  ctx.fillStyle = wall;
  roundRect(ctx, left, top, w, h, 3);
  ctx.fill();
  if (variant === 0) {
    // weatherboard lines
    ctx.strokeStyle = withAlpha(P.skyTop, 0.35);
    ctx.lineWidth = 0.8;
    ctx.beginPath();
    for (let ly = top + 6; ly < y; ly += 5) { ctx.moveTo(left + 1, ly); ctx.lineTo(left + w - 1, ly); }
    ctx.stroke();
  }
  // roof: gable
  ctx.fillStyle = roof;
  ctx.beginPath();
  ctx.moveTo(left - 5, top + 2);
  ctx.lineTo(x, top - h * 0.5);
  ctx.lineTo(left + w + 5, top + 2);
  ctx.closePath();
  ctx.fill();
  // corrugations on the roof
  ctx.strokeStyle = withAlpha(P.skyTop, 0.28);
  ctx.lineWidth = 0.8;
  ctx.beginPath();
  for (let i = 1; i < 6; i++) {
    const t = i / 6;
    ctx.moveTo(left - 5 + (x - left + 5) * t, top + 2 - (h * 0.5 + 2) * t);
    ctx.lineTo(left + w + 5 - (left + w + 5 - x) * t, top + 2 - (h * 0.5 + 2) * t);
  }
  ctx.stroke();
  if (variant === 0) {
    // verandah awning + posts
    ctx.fillStyle = roof;
    roundRect(ctx, left - 8, top + h * 0.3, w + 16, 4, 1);
    ctx.fill();
    ctx.strokeStyle = P.verandah;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(left - 6, top + h * 0.3 + 4); ctx.lineTo(left - 6, y);
    ctx.moveTo(left + w + 6, top + h * 0.3 + 4); ctx.lineTo(left + w + 6, y);
    ctx.stroke();
  }
  if (lit > 0.05) glow(ctx, x, top + h * 0.6, w * 0.7, P.windowGlow, 0.28 * lit);
  ctx.fillStyle = win;
  roundRect(ctx, left + w * 0.12, top + h * 0.42, w * 0.24, h * 0.32, 2); ctx.fill();
  roundRect(ctx, left + w * 0.64, top + h * 0.42, w * 0.24, h * 0.32, 2); ctx.fill();
  // window cross
  ctx.strokeStyle = withAlpha(P.skyTop, 0.45);
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(left + w * 0.24, top + h * 0.42); ctx.lineTo(left + w * 0.24, top + h * 0.74);
  ctx.moveTo(left + w * 0.76, top + h * 0.42); ctx.lineTo(left + w * 0.76, top + h * 0.74);
  ctx.stroke();
  ctx.fillStyle = P.door;
  roundRect(ctx, x - 6, top + h * 0.5, 12, h * 0.5, 2); ctx.fill();
  if (variant === 1 && jitter(seed, 3) > 0.5) {
    // a small solar array on the brick gable too
    ctx.fillStyle = P.solar;
    ctx.beginPath();
    ctx.moveTo(x + 4, top - h * 0.34);
    ctx.lineTo(x + 20, top - h * 0.14);
    ctx.lineTo(x + 30, top - h * 0.02);
    ctx.lineTo(x + 12, top - h * 0.22);
    ctx.closePath();
    ctx.fill();
  }
  if (cockatoo) drawCockatoo(ctx, P, x + 4, top - h * 0.5, 0.9);
}

// ---------------------------------------------------------------------------

export function drawGumTree(ctx, P, x, y, size = 60, seed = 1, lit = 0) {
  const trunkH = size * 0.55;
  const fill = mix(P.tree, P.treeLit, lit * 0.6);
  ctx.strokeStyle = fill;
  ctx.lineCap = 'round';
  ctx.lineWidth = Math.max(2, size * 0.06);
  // a gum trunk leans and forks
  const lean = (jitter(seed, 1) - 0.5) * size * 0.25;
  ctx.beginPath();
  ctx.moveTo(x, y);
  ctx.quadraticCurveTo(x + lean * 0.4, y - trunkH * 0.6, x + lean, y - trunkH);
  ctx.stroke();
  ctx.lineWidth = Math.max(1.5, size * 0.035);
  ctx.beginPath();
  ctx.moveTo(x + lean * 0.6, y - trunkH * 0.7);
  ctx.lineTo(x + lean - size * 0.22, y - trunkH - size * 0.12);
  ctx.moveTo(x + lean, y - trunkH);
  ctx.lineTo(x + lean + size * 0.2, y - trunkH - size * 0.18);
  ctx.stroke();
  // sparse, blobby canopy
  ctx.fillStyle = fill;
  const blobs = 7;
  for (let i = 0; i < blobs; i++) {
    const a = (i / blobs) * TAU + jitter(seed, i) * 0.8;
    const dist = size * (0.14 + jitter(seed, i + 10) * 0.22);
    const bx = x + lean + Math.cos(a) * dist;
    const by = y - trunkH - size * 0.12 + Math.sin(a) * dist * 0.6;
    const r = size * (0.11 + jitter(seed, i + 20) * 0.1);
    ctx.beginPath();
    ctx.ellipse(bx, by, r * 1.3, r, a, 0, TAU);
    ctx.fill();
  }
}

export function drawCockatoo(ctx, P, x, y, s = 1) {
  ctx.save();
  ctx.translate(x, y);
  ctx.scale(s, s);
  // body
  ctx.fillStyle = P.cockatoo;
  ctx.beginPath();
  ctx.ellipse(0, -5, 4.2, 6, 0.2, 0, TAU);
  ctx.fill();
  // head
  ctx.beginPath();
  ctx.arc(1.5, -12, 3.4, 0, TAU);
  ctx.fill();
  // crest (sulphur)
  ctx.strokeStyle = P.cockatooCrest;
  ctx.lineWidth = 1.4;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(1, -15); ctx.quadraticCurveTo(3, -21, 6, -20);
  ctx.moveTo(1.5, -15); ctx.quadraticCurveTo(0.5, -20, 2.5, -21);
  ctx.stroke();
  // beak & eye
  ctx.fillStyle = P.skyTop;
  ctx.beginPath();
  ctx.moveTo(4.6, -12); ctx.lineTo(6.6, -10.5); ctx.lineTo(4.4, -9.6); ctx.closePath();
  ctx.fill();
  ctx.beginPath(); ctx.arc(3, -13, 0.7, 0, TAU); ctx.fill();
  // tail
  ctx.fillStyle = P.cockatoo;
  ctx.beginPath();
  ctx.moveTo(-3, -1); ctx.lineTo(-7, 3); ctx.lineTo(-1, 0.5); ctx.closePath();
  ctx.fill();
  ctx.restore();
}

export function drawBell(ctx, P, x, y, r = 8, lit = 0) {
  if (lit > 0.05) glow(ctx, x, y, r * 3, P.bell, 0.35 * lit);
  ctx.fillStyle = P.bell;
  ctx.beginPath();
  ctx.moveTo(x - r, y + r * 0.7);
  ctx.quadraticCurveTo(x - r * 0.9, y - r * 0.6, x, y - r);
  ctx.quadraticCurveTo(x + r * 0.9, y - r * 0.6, x + r, y + r * 0.7);
  ctx.closePath();
  ctx.fill();
  ctx.fillRect(x - r * 1.1, y + r * 0.7, r * 2.2, r * 0.3);
  ctx.fillStyle = P.skyTop;
  ctx.beginPath(); ctx.arc(x, y + r * 1.05, r * 0.28, 0, TAU); ctx.fill();
  ctx.fillStyle = P.bell;
  ctx.beginPath(); ctx.arc(x, y - r * 1.05, r * 0.22, 0, TAU); ctx.fill();
}

export function drawBauble(ctx, P, x, y, r = 14, charge = 0, t = 0) {
  glow(ctx, x, y, r * 2.6, P.bauble, 0.25 + charge * 0.35);
  ctx.fillStyle = P.bauble;
  ctx.beginPath(); ctx.arc(x, y, r, 0, TAU); ctx.fill();
  // highlight
  ctx.fillStyle = withAlpha(P.white, 0.35);
  ctx.beginPath(); ctx.ellipse(x - r * 0.35, y - r * 0.4, r * 0.28, r * 0.18, -0.6, 0, TAU); ctx.fill();
  // lightning bolt = battery
  ctx.fillStyle = mix(P.white, P.star, 0.5 + charge * 0.5);
  ctx.beginPath();
  ctx.moveTo(x + r * 0.15, y - r * 0.6);
  ctx.lineTo(x - r * 0.3, y + r * 0.1);
  ctx.lineTo(x + r * 0.02, y + r * 0.1);
  ctx.lineTo(x - r * 0.15, y + r * 0.6);
  ctx.lineTo(x + r * 0.3, y - r * 0.1);
  ctx.lineTo(x - r * 0.02, y - r * 0.1);
  ctx.closePath();
  ctx.fill();
  // cap
  ctx.fillStyle = P.baubleCap;
  roundRect(ctx, x - r * 0.3, y - r - r * 0.35, r * 0.6, r * 0.4, 2);
  ctx.fill();
  if (charge > 0) {
    ctx.strokeStyle = withAlpha(P.star, 0.9);
    ctx.lineWidth = 2.5;
    ctx.beginPath();
    ctx.arc(x, y, r + 5, -Math.PI / 2, -Math.PI / 2 + TAU * charge);
    ctx.stroke();
  }
  if (t) {
    // a gentle sparkle orbit
    const a = t * 0.004;
    ctx.fillStyle = P.white;
    ctx.beginPath(); ctx.arc(x + Math.cos(a) * (r + 8), y + Math.sin(a) * (r + 8), 1.5, 0, TAU); ctx.fill();
  }
}

export function drawStar(ctx, P, x, y, r = 10, t = 0, collected = false) {
  if (collected) return;
  const pulse = 1 + Math.sin(t * 0.005) * 0.12;
  glow(ctx, x, y, r * 3.2 * pulse, P.star, 0.45);
  ctx.fillStyle = P.star;
  starPath(ctx, x, y, r * pulse, 5, 0.48);
  ctx.fill();
  ctx.fillStyle = withAlpha(P.white, 0.7);
  starPath(ctx, x, y, r * 0.45 * pulse, 5, 0.48);
  ctx.fill();
}

export function drawTangle(ctx, P, x, y, r = 10) {
  ctx.strokeStyle = P.tangle;
  ctx.lineWidth = 2.2;
  ctx.lineCap = 'round';
  ctx.beginPath();
  for (let i = 0; i <= 24; i++) {
    const a = (i / 24) * TAU * 2;
    const rr = r * (0.55 + 0.45 * Math.sin(i * 1.7));
    const px = x + Math.cos(a) * rr;
    const py = y + Math.sin(a * 1.3) * rr * 0.7;
    if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
  }
  ctx.stroke();
}

// ---------------------------------------------------------------------------
// Decorations. Matched by keyword so the ids in daily.js need not be known in
// advance: the brief names them and the keywords come from the brief.

const DECO_KEYS = [
  ['wattle', 'wattle'], ['wreath', 'wreath'], ['cricket', 'cricket'], ['barbecue', 'bbq'], ['bbq', 'bbq'],
  ['cockatoo', 'cockatoo'], ['caravan', 'caravan'], ['solar', 'solar'], ['tree', 'tree'],
  ['balcony', 'balcony'], ['sunset', 'sunset'],
];

export function decoKind(id) {
  const s = String(id ?? '').toLowerCase();
  for (const [k, v] of DECO_KEYS) if (s.includes(k)) return v;
  return 'star';
}

export function drawDecoration(ctx, P, id, x, y, size = 24, t = 0) {
  const kind = decoKind(id);
  const s = size / 24;
  ctx.save();
  ctx.translate(x, y);
  ctx.scale(s, s);
  switch (kind) {
    case 'wattle': {
      glow(ctx, 0, 0, 22, P.star, 0.5);
      ctx.fillStyle = P.bulb5;
      starPath(ctx, 0, -2, 11, 5, 0.5);
      ctx.fill();
      ctx.fillStyle = P.bulb3;
      for (let i = 0; i < 5; i++) { ctx.beginPath(); ctx.arc(-8 + i * 4, 9, 2.2, 0, TAU); ctx.fill(); }
      break;
    }
    case 'wreath': {
      ctx.strokeStyle = P.bulb3;
      ctx.lineWidth = 5;
      ctx.beginPath(); ctx.arc(0, 0, 9, 0, TAU); ctx.stroke();
      ctx.fillStyle = mix(P.bulb3, P.white, 0.3);
      for (let i = 0; i < 8; i++) {
        const a = (i / 8) * TAU;
        ctx.beginPath(); ctx.ellipse(Math.cos(a) * 9, Math.sin(a) * 9, 4, 1.6, a + 0.8, 0, TAU); ctx.fill();
      }
      ctx.fillStyle = P.bulb2;
      ctx.beginPath(); ctx.arc(0, -9, 2.6, 0, TAU); ctx.fill();
      break;
    }
    case 'cricket': {
      ctx.fillStyle = P.bulb5;
      roundRect(ctx, -4, -12, 6, 20, 2); ctx.fill();
      ctx.fillStyle = P.door;
      roundRect(ctx, -3, 8, 4, 5, 1); ctx.fill();
      ctx.fillStyle = P.bulb2;
      ctx.beginPath(); ctx.arc(8, 6, 4, 0, TAU); ctx.fill();
      ctx.strokeStyle = P.white; ctx.lineWidth = 1;
      ctx.beginPath(); ctx.arc(8, 6, 3, 0.4, 2.7); ctx.stroke();
      // stumps
      ctx.strokeStyle = P.cableActive; ctx.lineWidth = 1.4;
      ctx.beginPath();
      for (let i = -1; i <= 1; i++) { ctx.moveTo(-10 + i * 2.5, -4); ctx.lineTo(-10 + i * 2.5, 12); }
      ctx.stroke();
      break;
    }
    case 'bbq': {
      ctx.fillStyle = P.roofDark;
      roundRect(ctx, -11, -4, 22, 8, 3); ctx.fill();
      ctx.strokeStyle = P.cable; ctx.lineWidth = 1.6;
      ctx.beginPath(); ctx.moveTo(-7, 4); ctx.lineTo(-9, 12); ctx.moveTo(7, 4); ctx.lineTo(9, 12); ctx.stroke();
      ctx.fillStyle = P.spark;
      for (let i = 0; i < 3; i++) { ctx.beginPath(); ctx.arc(-6 + i * 6, -3 + Math.sin(t * 0.01 + i) * 0.5, 2.4, 0, TAU); ctx.fill(); }
      ctx.strokeStyle = withAlpha(P.white, 0.5); ctx.lineWidth = 1.2;
      ctx.beginPath();
      for (let i = 0; i < 3; i++) { ctx.moveTo(-6 + i * 6, -7); ctx.quadraticCurveTo(-4 + i * 6, -10, -6 + i * 6, -13); }
      ctx.stroke();
      break;
    }
    case 'cockatoo': {
      drawCockatoo(ctx, P, 0, 12, 1.15);
      ctx.fillStyle = P.bulb2;
      ctx.beginPath(); ctx.arc(2, -3, 2, 0, TAU); ctx.fill();
      break;
    }
    case 'caravan': {
      ctx.fillStyle = P.bulb1;
      roundRect(ctx, -12, -8, 24, 14, 5); ctx.fill();
      ctx.fillStyle = P.bulb4;
      roundRect(ctx, -8, -5, 7, 5, 1.5); ctx.fill();
      roundRect(ctx, 2, -5, 7, 5, 1.5); ctx.fill();
      ctx.fillStyle = P.skyTop;
      ctx.beginPath(); ctx.arc(-5, 8, 3, 0, TAU); ctx.fill();
      ctx.beginPath(); ctx.arc(6, 8, 3, 0, TAU); ctx.fill();
      ctx.fillStyle = P.bulb2;
      roundRect(ctx, -12, 0, 24, 2.5, 1); ctx.fill();
      break;
    }
    case 'solar': {
      glow(ctx, 0, -3, 20, P.star, 0.45);
      ctx.fillStyle = P.star;
      starPath(ctx, 0, -3, 10, 5, 0.5);
      ctx.fill();
      ctx.fillStyle = P.solar;
      roundRect(ctx, -11, 7, 22, 6, 1); ctx.fill();
      ctx.strokeStyle = withAlpha(P.white, 0.4); ctx.lineWidth = 0.8;
      ctx.beginPath(); ctx.moveTo(-4, 7); ctx.lineTo(-4, 13); ctx.moveTo(4, 7); ctx.lineTo(4, 13); ctx.stroke();
      break;
    }
    case 'tree': {
      ctx.fillStyle = P.bulb3;
      ctx.beginPath(); ctx.moveTo(0, -13); ctx.lineTo(9, 0); ctx.lineTo(-9, 0); ctx.closePath(); ctx.fill();
      ctx.beginPath(); ctx.moveTo(0, -6); ctx.lineTo(11, 8); ctx.lineTo(-11, 8); ctx.closePath(); ctx.fill();
      ctx.fillStyle = P.door;
      ctx.fillRect(-2, 8, 4, 5);
      const cols = [P.bulb2, P.bulb5, P.bulb4, P.bulb1];
      for (let i = 0; i < 6; i++) {
        ctx.fillStyle = cols[i % 4];
        ctx.beginPath(); ctx.arc(-6 + (i % 3) * 6 + (i > 2 ? -1 : 1), -4 + Math.floor(i / 3) * 8, 1.8, 0, TAU); ctx.fill();
      }
      ctx.fillStyle = P.star;
      starPath(ctx, 0, -13, 3.5, 5, 0.5); ctx.fill();
      break;
    }
    case 'balcony': {
      ctx.strokeStyle = P.cable; ctx.lineWidth = 1.5;
      ctx.beginPath(); ctx.moveTo(-12, -6); ctx.quadraticCurveTo(0, 2, 12, -6); ctx.stroke();
      const cols = [P.bulb1, P.bulb2, P.bulb3, P.bulb4, P.bulb5];
      for (let i = 0; i < 5; i++) {
        const px = -10 + i * 5;
        const py = -6 + (1 - Math.abs(i - 2) / 2) * 4 + 4;
        glow(ctx, px, py, 5, cols[i], 0.5);
        ctx.fillStyle = cols[i];
        ctx.beginPath(); ctx.arc(px, py, 2, 0, TAU); ctx.fill();
      }
      ctx.strokeStyle = P.verandah; ctx.lineWidth = 1.5;
      ctx.beginPath();
      for (let i = -3; i <= 3; i++) { ctx.moveTo(i * 4, 6); ctx.lineTo(i * 4, 13); }
      ctx.moveTo(-13, 6); ctx.lineTo(13, 6);
      ctx.stroke();
      break;
    }
    case 'sunset': {
      const g = ctx.createLinearGradient(0, -12, 0, 12);
      g.addColorStop(0, P.skyMid);
      g.addColorStop(1, P.skyHorizon);
      ctx.fillStyle = g;
      roundRect(ctx, -12, -12, 24, 24, 5); ctx.fill();
      ctx.fillStyle = P.spark;
      ctx.beginPath(); ctx.arc(0, 6, 6, Math.PI, TAU); ctx.fill();
      ctx.fillStyle = P.skyTop;
      ctx.fillRect(-12, 6, 24, 6);
      break;
    }
    default: {
      ctx.fillStyle = P.star;
      starPath(ctx, 0, 0, 10, 5, 0.5);
      ctx.fill();
    }
  }
  ctx.restore();
}

// ---------------------------------------------------------------------------
// Moment icons over a completed home's roof. Keyword-matched against the moment
// id / caption for the same reason as the decorations.

export function momentKind(moment) {
  const s = String(moment ?? '').toLowerCase();
  if (/call|video|nan|phone|screen/.test(s)) return 'call';
  if (/barbecue|bbq|snag|grill/.test(s)) return 'bbq';
  if (/ev|road|trip|car|drive|caravan|charg/.test(s)) return 'car';
  if (/solar|battery|panel|sun/.test(s)) return 'solar';
  if (/cricket|bat|ball|backyard/.test(s)) return 'cricket';
  if (/pool|swim|beach|hose|sprinkler/.test(s)) return 'water';
  if (/carol|music|sing|song|dance/.test(s)) return 'music';
  if (/present|gift|wrap/.test(s)) return 'gift';
  if (/dinner|table|lunch|feast|prawn|pav|dessert|family|meal/.test(s)) return 'dinner';
  return 'heart';
}

export function drawMomentIcon(ctx, P, moment, x, y, r = 12) {
  const kind = momentKind(moment);
  ctx.save();
  ctx.translate(x, y);
  const s = r / 12;
  ctx.scale(s, s);
  glow(ctx, 0, 0, 22, P.windowGlow, 0.5);
  ctx.fillStyle = P.windowGlow;
  ctx.beginPath(); ctx.arc(0, 0, 12, 0, TAU); ctx.fill();
  ctx.fillStyle = P.skyTop;
  ctx.strokeStyle = P.skyTop;
  ctx.lineWidth = 1.6;
  ctx.lineCap = 'round';
  switch (kind) {
    case 'call':
      roundRect(ctx, -6, -4.5, 12, 9, 1.5); ctx.fill();
      ctx.fillStyle = P.windowGlow;
      ctx.beginPath(); ctx.arc(-1.5, -0.5, 1.8, 0, TAU); ctx.fill();
      ctx.fillStyle = P.skyTop;
      ctx.beginPath(); ctx.moveTo(6, -2); ctx.lineTo(9, -4); ctx.lineTo(9, 4); ctx.lineTo(6, 2); ctx.closePath(); ctx.fill();
      break;
    case 'bbq':
      roundRect(ctx, -7, -2, 14, 5, 2); ctx.fill();
      ctx.beginPath(); ctx.moveTo(-4, 3); ctx.lineTo(-5, 8); ctx.moveTo(4, 3); ctx.lineTo(5, 8); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(-4, -4); ctx.quadraticCurveTo(-2, -6, -4, -8); ctx.moveTo(0, -4); ctx.quadraticCurveTo(2, -6, 0, -8); ctx.moveTo(4, -4); ctx.quadraticCurveTo(6, -6, 4, -8); ctx.stroke();
      break;
    case 'car':
      roundRect(ctx, -8, -1, 16, 6, 2); ctx.fill();
      roundRect(ctx, -5, -5, 10, 5, 2); ctx.fill();
      ctx.beginPath(); ctx.arc(-4.5, 6, 2, 0, TAU); ctx.arc(4.5, 6, 2, 0, TAU); ctx.fill();
      ctx.fillStyle = P.windowGlow;
      ctx.beginPath(); ctx.moveTo(1, -4); ctx.lineTo(-1, 0); ctx.lineTo(0.5, 0); ctx.lineTo(-1, 3); ctx.lineTo(2, -1); ctx.lineTo(0.5, -1); ctx.closePath(); ctx.fill();
      break;
    case 'solar':
      ctx.beginPath(); ctx.arc(0, -3, 3.5, 0, TAU); ctx.fill();
      ctx.beginPath();
      for (let i = 0; i < 8; i++) { const a = (i / 8) * TAU; ctx.moveTo(Math.cos(a) * 5.5, -3 + Math.sin(a) * 5.5); ctx.lineTo(Math.cos(a) * 7.5, -3 + Math.sin(a) * 7.5); }
      ctx.stroke();
      roundRect(ctx, -7, 5, 14, 3.5, 1); ctx.fill();
      break;
    case 'cricket':
      ctx.save(); ctx.rotate(-0.5);
      roundRect(ctx, -2, -9, 4, 14, 1.5); ctx.fill();
      ctx.restore();
      ctx.beginPath(); ctx.arc(5, 5, 2.6, 0, TAU); ctx.fill();
      break;
    case 'water':
      ctx.beginPath();
      ctx.moveTo(-8, 1); ctx.quadraticCurveTo(-5, -3, -2, 1); ctx.quadraticCurveTo(1, 5, 4, 1); ctx.quadraticCurveTo(6, -2, 8, 1);
      ctx.moveTo(-8, 6); ctx.quadraticCurveTo(-5, 2, -2, 6); ctx.quadraticCurveTo(1, 10, 4, 6); ctx.quadraticCurveTo(6, 3, 8, 6);
      ctx.stroke();
      ctx.beginPath(); ctx.arc(0, -6, 2.5, 0, TAU); ctx.fill();
      break;
    case 'music':
      ctx.beginPath(); ctx.arc(-3, 5, 2.8, 0, TAU); ctx.arc(5, 3, 2.8, 0, TAU); ctx.fill();
      ctx.beginPath(); ctx.moveTo(-1, 5); ctx.lineTo(-1, -6); ctx.lineTo(7, -8); ctx.lineTo(7, 3); ctx.stroke();
      break;
    case 'gift':
      roundRect(ctx, -7, -3, 14, 10, 1.5); ctx.fill();
      ctx.fillStyle = P.windowGlow;
      ctx.fillRect(-1, -3, 2, 10); ctx.fillRect(-7, 0, 14, 1.5);
      ctx.strokeStyle = P.skyTop;
      ctx.beginPath(); ctx.moveTo(0, -3); ctx.quadraticCurveTo(-5, -9, -1, -7); ctx.moveTo(0, -3); ctx.quadraticCurveTo(5, -9, 1, -7); ctx.stroke();
      break;
    case 'dinner':
      ctx.beginPath(); ctx.arc(0, 1, 6, 0, TAU); ctx.fill();
      ctx.fillStyle = P.windowGlow;
      ctx.beginPath(); ctx.arc(0, 1, 3.5, 0, TAU); ctx.fill();
      ctx.beginPath(); ctx.moveTo(-9, -5); ctx.lineTo(-9, 7); ctx.moveTo(9, -5); ctx.lineTo(9, 7); ctx.stroke();
      break;
    default:
      ctx.beginPath();
      ctx.moveTo(0, 6);
      ctx.bezierCurveTo(-9, -1, -5, -9, 0, -4);
      ctx.bezierCurveTo(5, -9, 9, -1, 0, 6);
      ctx.fill();
  }
  ctx.restore();
}

export { roundRect, starPath };
